import uniq from 'lodash/uniq';
import mms from '~/vendor/mms';
import CatalogTypes from '~/constants/types/CatalogTypes';
import {getStyle} from '~/selectors/CatalogSelectors';
import {getCurrentColorId} from '~/selectors/ProductSelectors';
import {loadComplements} from '~/actions/ComplementsActions';
import {loadCompatibleStyles} from '~/actions/CompatibleStylesActions';
import {createAction} from 'redux-actions';

export function styleLoadError(styleId, error) {
  return {
    type: CatalogTypes.STYLE_LOAD_ERROR,
    payload: {
      error,
      styleId
    },
    error: true
  };
}

export const styleLoaded = createAction(
  CatalogTypes.STYLE_LOADED,
  undefined,
  ({style}) => ({styleId: style.id})
);

const handleStyleResponse = (dispatch, state) => style => {
  const currentColorId = getCurrentColorId(state);
  dispatch(styleLoaded({currentColorId, style}));

  if (style !== null && !style.specialty) {
    dispatch(loadComplements(style.id));
    dispatch(loadCompatibleStyles(style.id));
  }

  return style;
};

const handleStyleError = (dispatch, styleId) => error => {
  dispatch(styleLoadError(styleId, error));
  return null;
};

export function fetchStyle(styleId) {
  return (dispatch, getState) => {
    const state = getState();
    const style = getStyle(state, {styleId});

    if (style && (!style.errors || style.errors.length === 0)) {
      return Promise.resolve(style);
    }

    dispatch({
      type: CatalogTypes.FETCH_STYLE,
      payload: styleId
    });

    return mms
      .getStyle(styleId)
      .then(handleStyleResponse(dispatch, state))
      .catch(handleStyleError(dispatch, styleId));
  };
}

export function fetchStyleFromColor(colorId) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch({
      type: CatalogTypes.FETCH_STYLE,
      payload: colorId
    });

    return mms
      .getStylesFromColors([colorId])
      .then(colors => colors[0])
      .then(handleStyleResponse(dispatch, state))
      .catch(handleStyleError(dispatch, colorId));
  };
}

export const loadStyles =
  (
    styleIds,
    withComplements = false,
    withCompatibleStyles = false,
    ignoreLoadErrorNotification = false
  ) =>
  dispatch => {
    const uniqStyleIds = uniq(styleIds);
    return new Promise((resolve, reject) => {
      dispatch(fetchStyles(uniqStyleIds));
      return mms
        .getStyles(...uniqStyleIds)
        .then(styles => {
          if (styles && styles.length === uniqStyleIds.length) {
            dispatch(stylesSuccess(styles));

            if (withComplements) {
              uniqStyleIds.forEach(id => {
                dispatch(loadComplements(id));
              });
            }

            if (withCompatibleStyles) {
              uniqStyleIds.forEach(id => {
                dispatch(loadCompatibleStyles(id));
              });
            }
          } else {
            dispatch(
              stylesError({
                styleIds: uniqStyleIds,
                retrievedIds: styles.map(style => style && style.id),
                ignoreLoadErrorNotification
              })
            );
          }
          resolve(styles);
        })
        .catch(err => {
          err.styleIds = uniqStyleIds;
          dispatch(stylesError({err, ignoreLoadErrorNotification}));
          reject(err);
        });
    });
  };

export const fetchStyles = createAction(CatalogTypes.FETCH_STYLES);

export const stylesSuccess = createAction(
  CatalogTypes.STYLES_SUCCESS,
  undefined,
  styles => styles.map(s => s.id)
);

export const stylesError = createAction(CatalogTypes.STYLES_ERROR);

export const loadCatalogDesign =
  ({catalogCID, catalogCIDProductColorId}) =>
  dispatch => {
    dispatch({
      type: CatalogTypes.LOAD_CATALOG_DESIGN,
      payload: {
        catalogCID,
        catalogCIDProductColorId
      }
    });
  };

export function loadStyleVariants(styleIds) {
  return (dispatch, getState) => {
    const state = getState();
    const variantStyleIds = styleIds.reduce((variantIds, styleId) => {
      const style = getStyle(state, {styleId});
      if (style && style.variants) {
        return variantIds.concat(
          style.variants.map(variant => variant.style_id.toString())
        );
      }
      return variantIds;
    }, []);
    const unloadedVariantStyleIds = uniq(variantStyleIds).filter(
      styleId => !getStyle(getState(), {styleId})
    );
    if (unloadedVariantStyleIds.length > 0) {
      return dispatch(loadStyles(unloadedVariantStyleIds, false, false, true));
    }
  };
}
